import React, { useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { DropdownFloating } from '@components/ui/dropdown-floating';
import { cn } from '@utils/index';
import { useCompareStore } from 'src/store/compare';

export type ViewMode = 'grid' | 'list' | 'tab';

interface MedicalPlansControlsProps {
  form: UseFormReturn<Record<string, unknown>>;
  viewMode: ViewMode;
  onSortChange?: (key: string, direction: 'asc' | 'desc') => void;
  setViewMode: (mode: ViewMode) => void;
  compareName: string;
  isHidePlansBtn?: boolean;
  plans: { id: string; title: string }[];
  selectedPlansLocal?: { id: string; title: string }[];
  onOpenCompare?: () => void;
}

export const MedicalPlansControls = ({
  form,
  onSortChange,
  viewMode,
  setViewMode,
  compareName,
  isHidePlansBtn,
  plans,
  selectedPlansLocal,
  onOpenCompare,
}: MedicalPlansControlsProps) => {
  const { selectedPlans, removePlan } = useCompareStore();

  const filteredSelectedPlans = React.useMemo(() => {
    const planIds = new Set(plans.map((p) => p.id));
    return selectedPlans.filter((planId) => planIds.has(planId));
  }, [selectedPlans, plans]);

  const [sortValue, setSortValue] = useState('Lowest Monthly Premium');

  const handleSortChange = (newSortValue: string) => {
    setSortValue(newSortValue);
  };

  useEffect(() => {
    if (onSortChange) {
      let key = '';
      let direction: 'asc' | 'desc' = 'asc';

      if (sortValue === 'Lowest Monthly Premium') {
        key = 'averagePrice';
        direction = 'asc';
      } else if (sortValue === 'Highest Monthly Premium') {
        key = 'averagePrice';
        direction = 'desc';
      } else if (sortValue === 'Lowest Deductible') {
        key = 'deductibleIndividualInNetwork';
        direction = 'asc';
      } else if (sortValue === 'Highest Deductible') {
        key = 'deductibleIndividualInNetwork';
        direction = 'desc';
      } else if (
        sortValue === 'Most Popular' ||
        sortValue === 'Highest Rated'
      ) {
        key = 'name';
        direction = 'asc';
      }
      onSortChange(key, direction);
    }
  }, [sortValue, onSortChange]);

  // useEffect(() => {
  //   if (plans.length < 2) {
  //     setViewMode('list');
  //   }
  // }, [plans.length, setViewMode]);

  return (
    <div className="flex flex-col gap-4 mt-6">
      <div className="flex flex-row justify-between items-center gap-4 max-md:flex-col max-md:items-start">
        {plans.length > 1 && (
          <div className="w-64 max-md:w-full h-full min-h-12">
            <DropdownFloating
              id={`sort-select`}
              options={[
                {
                  label: 'Lowest Monthly Premium',
                  value: 'Lowest Monthly Premium',
                },
                { label: 'Lowest Deductible', value: 'Lowest Deductible' },
                {
                  label: 'Highest Monthly Premium',
                  value: 'Highest Monthly Premium',
                },
                { label: 'Highest Deductible', value: 'Highest Deductible' },
                { label: 'Most Popular', value: 'Most Popular' },
                { label: 'Highest Rated', value: 'Highest Rated' },
              ]}
              value={sortValue}
              label="Sort by default"
              onChange={(val) => handleSortChange(val)}
              inputClass="max-md:h-12"
            />
          </div>
        )}
        <div className="flex gap-4 items-center justify-normal max-md:justify-between max-md:gap-0 max-md:w-full">
          <span className="text-xl font-medium capitalize">
            {viewMode} view
          </span>
          <div className="bg-[#f0eee9] flex gap-1 p-1.5 rounded-lg">
            {['grid', 'list', 'tab'].map((mode, index) => (
              <button
                key={mode + '_' + index}
                type="button"
                className={cn(
                  `p-2 cursor-pointer rounded-[5px] ${viewMode === mode ? 'bg-[#31313e]' : ''}`,
                  mode === 'list' ? 'max-md:hidden' : ''
                )}
                onClick={() => setViewMode(mode as ViewMode)}
              >
                <img
                  src={`/images/${mode}view.svg`}
                  className={cn(
                    'w-4.5 h-4.5 transition',
                    viewMode === mode ? 'invert' : ''
                  )}
                  alt={`${mode} view`}
                />
              </button>
            ))}
          </div>
        </div>
      </div>
      {(selectedPlansLocal?.length === 2 && !isHidePlansBtn
        ? selectedPlansLocal
        : filteredSelectedPlans
      ).length > 0 && (
        <div className="flex flex-col gap-2 mt-4.5 pr-0">
          <div className="flex items-center gap-3 lg:gap-2 justify-between">
            <div className="flex flex-wrap items-center gap-2">
              {!!filteredSelectedPlans.length && (
                <span className="xl:text-xl text-base font-semibold">
                  Compare:
                </span>
              )}
              {filteredSelectedPlans.map((planId: string) => {
                const plan = plans.find((p) => p.id === planId);
                return (
                  <div
                    key={planId}
                    className="flex items-center gap-5 max-lg:gap-2 justify-between bg-view-section px-3 py-1 rounded-full"
                  >
                    <span className="text-xs font-semibold text-inherit">
                      {plan?.title || planId}
                    </span>
                    <button
                      onClick={() => removePlan(planId)}
                      className="text-black cursor-pointer font-semibold text-base"
                    >
                      x
                    </button>
                  </div>
                );
              })}
              {filteredSelectedPlans.length >= 3 && (
                <p className="mt-3">You may compare up to 3 plans at a time.</p>
              )}
            </div>
            <button
              className="mb-auto bg-destructive disabled:opacity-70 uppercase text-white cursor-pointer px-6 py-2 rounded-full w-31 text-sm font-semibold hover:bg-destructive/90"
              onClick={() => {
                if (onOpenCompare) onOpenCompare();
              }}
              disabled={
                selectedPlansLocal?.length === 2
                  ? false
                  : filteredSelectedPlans.length < 2
              }
            >
              Compare
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
